import React from 'react'
import { motion } from 'framer-motion'
import { Music, Users, Calendar, ExternalLink, Star } from 'lucide-react'

const FeaturedArtists = () => {
  const artists = [
    {
      id: 1,
      name: 'Sarah Mitchell',
      genre: 'Indie Folk',
      bio: 'Wichita-born singer-songwriter with a soulful voice and heartfelt lyrics.',
      image: 'https://images.pexels.com/photos/1587927/pexels-photo-1587927.jpeg?auto=compress&cs=tinysrgb&w=600',
      followers: 1234,
      upcomingShows: 3,
      rating: 4.9,
      verified: true,
      featured: true,
    },
    {
      id: 2,
      name: 'Thunder Road',
      genre: 'Classic Rock',
      bio: 'High-energy rock band bringing the thunder to Wichita stages.',
      image: 'https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg?auto=compress&cs=tinysrgb&w=600',
      followers: 892,
      upcomingShows: 2,
      rating: 4.7,
      verified: true,
    },
    {
      id: 3,
      name: 'Jazz Collective',
      genre: 'Contemporary Jazz',
      bio: 'Smooth jazz ensemble featuring Wichita\'s finest musicians.',
      image: 'https://images.pexels.com/photos/1540406/pexels-photo-1540406.jpeg?auto=compress&cs=tinysrgb&w=600',
      followers: 567,
      upcomingShows: 4,
      rating: 4.8,
      verified: false,
    },
    {
      id: 4,
      name: 'Electric Dreams',
      genre: 'Electronic',
      bio: 'Cutting-edge electronic music producer and live performer.',
      image: 'https://images.pexels.com/photos/1105666/pexels-photo-1105666.jpeg?auto=compress&cs=tinysrgb&w=600',
      followers: 2156,
      upcomingShows: 1,
      rating: 4.6,
      verified: true,
    },
  ]

  return (
    <section className="section-padding py-20">
      <div className="container-max">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl lg:text-5xl font-display font-bold text-white mb-6">
            Featured
            <span className="text-gradient block">Local Artists</span>
          </h2>
          <p className="text-xl text-white/70 max-w-3xl mx-auto">
            Discover the incredible talent that makes Wichita's music scene so vibrant and unique.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {artists.map((artist, index) => (
            <motion.div
              key={artist.id}
              className={`glass-card overflow-hidden hover:bg-white/10 transition-all duration-300 cursor-pointer group ${
                artist.featured ? 'md:col-span-2 lg:col-span-2' : ''
              }`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
            >
              {artist.featured && (
                <div className="absolute top-4 left-4 z-10 bg-gradient-to-r from-le-accent to-le-accent-2 text-white text-xs px-3 py-1 rounded-full font-medium">
                  Featured Artist
                </div>
              )}

              <div className="relative h-64 overflow-hidden">
                <img
                  src={artist.image}
                  alt={artist.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                
                {artist.verified && (
                  <div className="absolute top-4 right-4 w-8 h-8 bg-le-accent rounded-full flex items-center justify-center">
                    <Star size={16} className="text-white fill-current" />
                  </div>
                )}

                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center space-x-2 text-white text-sm mb-2">
                    <div className="flex items-center space-x-1">
                      <Star size={14} className="text-yellow-400 fill-current" />
                      <span>{artist.rating}</span>
                    </div>
                    <span className="bg-black/40 backdrop-blur-sm px-2 py-1 rounded-full">
                      {artist.genre}
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-display font-semibold text-xl text-white group-hover:text-le-accent transition-colors duration-300">
                    {artist.name}
                  </h3>
                  <button className="text-white/60 hover:text-le-accent transition-colors duration-200">
                    <ExternalLink size={18} />
                  </button>
                </div>

                <p className="text-white/70 mb-4 leading-relaxed">
                  {artist.bio}
                </p>

                <div className="grid grid-cols-2 gap-4 text-sm text-white/60 mb-6">
                  <div className="flex items-center space-x-2">
                    <Users size={14} />
                    <span>{artist.followers.toLocaleString()} followers</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Calendar size={14} />
                    <span>{artist.upcomingShows} upcoming shows</span>
                  </div>
                </div>

                <div className="flex space-x-3">
                  <button className="flex-1 btn-primary text-sm py-2">
                    View Profile
                  </button>
                  <button className="flex-1 btn-secondary text-sm py-2">
                    Follow
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <button className="btn-secondary text-lg px-8 py-4">
            Discover More Artists
          </button>
        </motion.div>
      </div>
    </section>
  )
}

export default FeaturedArtists